import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Minus, Pencil, Check, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "@/components/ui/button";
import type { GraphDiffProposal, GraphOperation } from "../../api/buildAgent";

interface GraphDiffPreviewProps {
  proposal: GraphDiffProposal;
  status: "pending" | "accepted" | "rejected";
  onAccept: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
}

function opLabel(op: GraphOperation): string {
  switch (op.type) {
    case "add_node": return `Add node: ${(op.node?.data as any)?.name ?? op.node?.id ?? "unnamed"}`;
    case "remove_node": return `Remove node: ${op.nodeId}`;
    case "update_node": return `Update node: ${op.nodeId}`;
    case "add_edge": return `Add edge: ${op.edge?.source} → ${op.edge?.target}${op.edge?.label ? ` (${op.edge.label})` : ""}`;
    case "remove_edge": return `Remove edge: ${op.edgeId}`;
    case "update_edge": return `Update edge: ${op.edgeId}`;
    default: return op.type;
  }
}

function opColor(op: GraphOperation): string {
  if (op.type.startsWith("add_")) return "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800";
  if (op.type.startsWith("remove_")) return "text-destructive bg-destructive/5 border-destructive/20";
  return "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800";
}

function OpIcon({ type }: { type: GraphOperation["type"] }) {
  if (type.startsWith("add_")) return <Plus className="h-3 w-3 shrink-0 mt-0.5" />;
  if (type.startsWith("remove_")) return <Minus className="h-3 w-3 shrink-0 mt-0.5" />;
  return <Pencil className="h-3 w-3 shrink-0 mt-0.5" />;
}

function OperationRow({ op }: { op: GraphOperation }) {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = op.node || op.edge || op.updates;
  const colorClass = opColor(op);

  return (
    <div className={cn("rounded border px-2.5 py-1.5 text-[11px]", colorClass)}>
      <button
        type="button"
        className="flex items-start gap-1.5 w-full text-left"
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        <OpIcon type={op.type} />
        <span className="flex-1 font-medium">{opLabel(op)}</span>
        {hasDetails && (
          expanded
            ? <ChevronDown className="h-3 w-3 shrink-0 mt-0.5 opacity-60" />
            : <ChevronRight className="h-3 w-3 shrink-0 mt-0.5 opacity-60" />
        )}
      </button>
      {expanded && hasDetails && (
        <pre className="mt-1.5 text-[10px] opacity-80 overflow-x-auto">
          {JSON.stringify(op.node ?? op.edge ?? op.updates, null, 2)}
        </pre>
      )}
    </div>
  );
}

export function GraphDiffPreview({ proposal, status, onAccept, onReject }: GraphDiffPreviewProps) {
  const { summary } = proposal;

  const summaryParts: string[] = [];
  if (summary.addNodes) summaryParts.push(`+${summary.addNodes} node${summary.addNodes > 1 ? "s" : ""}`);
  if (summary.removeNodes) summaryParts.push(`-${summary.removeNodes} node${summary.removeNodes > 1 ? "s" : ""}`);
  if (summary.updateNodes) summaryParts.push(`~${summary.updateNodes} node${summary.updateNodes > 1 ? "s" : ""}`);
  if (summary.addEdges) summaryParts.push(`+${summary.addEdges} edge${summary.addEdges > 1 ? "s" : ""}`);
  if (summary.removeEdges) summaryParts.push(`-${summary.removeEdges} edge${summary.removeEdges > 1 ? "s" : ""}`);
  if (summary.updateEdges) summaryParts.push(`~${summary.updateEdges} edge${summary.updateEdges > 1 ? "s" : ""}`);

  const isPending = status === "pending";

  return (
    <div className="rounded-lg border border-border bg-card/60 overflow-hidden mt-2">
      {/* Header */}
      <div className="flex items-start justify-between gap-2 px-3 py-2 border-b border-border bg-muted/30">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold text-foreground">{proposal.description}</p>
          {summaryParts.length > 0 && (
            <p className="text-[10px] text-muted-foreground mt-0.5">{summaryParts.join(" · ")}</p>
          )}
        </div>
        {status === "accepted" && (
          <span className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 shrink-0">
            <Check className="h-3 w-3" /> Applied
          </span>
        )}
        {status === "rejected" && (
          <span className="flex items-center gap-1 text-[10px] text-muted-foreground shrink-0">
            <X className="h-3 w-3" /> Rejected
          </span>
        )}
      </div>

      {/* Operations */}
      <div className="p-2 space-y-1">
        {proposal.operations.map((op, i) => (
          <OperationRow key={i} op={op} />
        ))}
      </div>

      {/* Actions */}
      {isPending && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-border">
          <Button
            size="sm"
            className="h-7 text-[11px] flex-1"
            onClick={() => onAccept(proposal.proposalId)}
          >
            <Check className="h-3 w-3 mr-1" />
            Accept
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-[11px]"
            onClick={() => onReject(proposal.proposalId)}
          >
            <X className="h-3 w-3 mr-1" />
            Reject
          </Button>
        </div>
      )}
    </div>
  );
}
